import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const OVERLAY_STYLES = {
  position: 'absolute',
  inset: 0,
  zIndex: 1000,
  background: '#111',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
};

function QRScanOverlayInner({ onClose, onScan }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const trackRef = useRef(null);
  const [torchOn, setTorchOn] = useState(false);
  const [flashColor, setFlashColor] = useState(null);
  const [statusLabel, setStatusLabel] = useState(null);
  const [cornerColor, setCornerColor] = useState('#1D9E75');
  const [showCheck, setShowCheck] = useState(false);
  const [showX, setShowX] = useState(false);
  const [scanLineTop, setScanLineTop] = useState(8);
  const scanLineDir = useRef(1);
  const scanLinePos = useRef(8);
  const decodedRef = useRef(false);

  useEffect(() => {
    let animFrame;
    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });
        streamRef.current = stream;
        const track = stream.getVideoTracks()[0];
        trackRef.current = track;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
        tick();
      } catch (e) {
        onClose();
      }
    }

    function tick() {
      animFrame = requestAnimationFrame(tick);
      rafRef.current = animFrame;

      scanLinePos.current += scanLineDir.current * 1.5;
      if (scanLinePos.current >= 202) scanLineDir.current = -1;
      if (scanLinePos.current <= 8) scanLineDir.current = 1;
      setScanLineTop(scanLinePos.current);

      if (decodedRef.current) return;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || video.readyState < 2) return;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      import('jsqr').then(({ default: jsQR }) => {
        if (decodedRef.current) return;
        const result = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert',
        });
        if (result) {
          decodedRef.current = true;
          cancelAnimationFrame(rafRef.current);
          handleDecode(result.data);
        }
      });
    }

    startCamera();
    return () => {
      cancelAnimationFrame(animFrame);
      stopStream();
    };
  }, []);

  function stopStream() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }

  function handleDecode(decoded) {
    if (decoded.includes('/inventory/scan/') || decoded.includes('/tool/')) {
      setFlashColor('rgba(29,158,117,0.38)');
      setCornerColor('#1D9E75');
      setShowCheck(true);
      setStatusLabel({ main: 'Got it', sub: onScan ? 'Adding to cart…' : 'Navigating…', mainColor: '#1D9E75' });
      setTimeout(() => {
        stopStream();
        if (onScan) {
          onScan(decoded);
          return;
        }
        try {
          const url = new URL(decoded);
          window.location.href = url.pathname;
        } catch {
          window.location.href = decoded;
        }
      }, 400);
      return;
    }
    // BARCODE HOOK — @zxing/library drops in here for UPC/EAN/Code128
    handleNoRead(decoded);
  }

  // Session 78a. `rejected` is the decoded payload, and passing it fixes a
  // copy bug that cost real diagnosis time.
  //
  // This function is reachable from exactly ONE place - handleDecode's fall
  // through - which means the camera ALWAYS read a code successfully before it
  // runs. There is no path here for "found nothing": the scan loop simply keeps
  // looking, silently, which is correct. So the red flash has only ever meant
  // "I read a code and it is not a Stede label."
  //
  // It said "No code found. Move closer or improve lighting."
  //
  // At Session 78a that sent Colby to check focus, distance and lighting on a
  // label his iPhone's own camera had just read perfectly - the one reading
  // that proves the label is fine and the reader is not. A warehouse in
  // September would have drawn the same wrong conclusion and blamed the
  // printer. Naming what was actually scanned turns a guess into a fact.
  function handleNoRead(rejected) {
    decodedRef.current = true;
    cancelAnimationFrame(rafRef.current);
    setFlashColor('rgba(226,75,74,0.38)');
    setCornerColor('#E24B4A');
    setShowX(true);
    // Truncated from the FRONT: the host and the route are what identify a
    // label, and they are what a person needs to see to know whether they
    // scanned the right thing. The id tail is the disposable half.
    const seen = String(rejected || '').replace(/^https?:\/\//, '');
    setStatusLabel({
      main: rejected ? 'Not a Stede label' : 'No code found',
      sub: rejected
        ? `Read: ${seen.length > 46 ? seen.slice(0, 46) + '…' : seen}`
        : 'Move closer or improve lighting',
      mainColor: '#E24B4A',
    });
    setTimeout(() => {
      setFlashColor(null);
      setCornerColor('#1D9E75');
      setShowX(false);
      setStatusLabel(null);
      decodedRef.current = false;
      scanLineDir.current = 1;
      scanLinePos.current = 8;
      rafRef.current = requestAnimationFrame(function tick() {
        rafRef.current = requestAnimationFrame(tick);
        scanLinePos.current += scanLineDir.current * 1.5;
        if (scanLinePos.current >= 202) scanLineDir.current = -1;
        if (scanLinePos.current <= 8) scanLineDir.current = 1;
        setScanLineTop(scanLinePos.current);
        if (decodedRef.current) return;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas || video.readyState < 2) return;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        import('jsqr').then(({ default: jsQR }) => {
          if (decodedRef.current) return;
          const result = jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: 'dontInvert',
          });
          if (result) {
            decodedRef.current = true;
            cancelAnimationFrame(rafRef.current);
            handleDecode(result.data);
          }
        });
      });
    }, rejected ? 2600 : 600);
  }

  async function toggleTorch() {
    const track = trackRef.current;
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn(v => !v);
    } catch {
      // torch not supported on this device — button becomes a no-op
    }
  }

  function handleCancel() {
    cancelAnimationFrame(rafRef.current);
    stopStream();
    onClose();
  }

  const cornerBase = {
    position: 'absolute',
    width: 36,
    height: 36,
    borderWidth: 3,
    borderStyle: 'solid',
    borderColor: 'transparent',
    transition: 'border-color 0.15s',
  };

  return (
    <div style={OVERLAY_STYLES}>
      <video ref={videoRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} playsInline muted />
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {flashColor && (
        <div style={{ position: 'absolute', inset: 0, background: flashColor, zIndex: 10, pointerEvents: 'none', transition: 'opacity 0.25s' }} />
      )}

      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.08) 30%, rgba(0,0,0,0.08) 70%, rgba(0,0,0,0.65) 100%)', zIndex: 2 }} />

      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3 }}>
        <div style={{ position: 'relative', width: 220, height: 220 }}>
          <div style={{ ...cornerBase, top: 0, left: 0, borderTopColor: cornerColor, borderLeftColor: cornerColor, borderRadius: '4px 0 0 0' }} />
          <div style={{ ...cornerBase, top: 0, right: 0, borderTopColor: cornerColor, borderRightColor: cornerColor, borderRadius: '0 4px 0 0' }} />
          <div style={{ ...cornerBase, bottom: 0, left: 0, borderBottomColor: cornerColor, borderLeftColor: cornerColor, borderRadius: '0 0 0 4px' }} />
          <div style={{ ...cornerBase, bottom: 0, right: 0, borderBottomColor: cornerColor, borderRightColor: cornerColor, borderRadius: '0 0 4px 0' }} />

          {!showCheck && !showX && (
            <div style={{ position: 'absolute', left: 8, right: 8, top: scanLineTop, height: 2, background: '#1D9E75', borderRadius: 1, boxShadow: '0 0 8px #1D9E75', opacity: 0.9 }} />
          )}
          {showCheck && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none"><path d="M14 34l12 12 24-24" stroke="#1D9E75" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
          )}
          {showX && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none"><path d="M20 20l24 24M44 20L20 44" stroke="#E24B4A" strokeWidth="5" strokeLinecap="round"/></svg>
            </div>
          )}
        </div>
      </div>

      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: '16px 16px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 4 }}>
        <div style={{ minWidth: 80 }}>
          <button onClick={handleCancel} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8, padding: '8px 12px', color: 'white', fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Cancel
          </button>
        </div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', fontWeight: 500, letterSpacing: '0.5px', textTransform: 'uppercase', textAlign: 'center', flex: 1 }}>Stede</div>
        <div style={{ minWidth: 80, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={toggleTorch} style={{ background: torchOn ? 'rgba(255,220,80,0.3)' : 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8, padding: '8px 12px', color: 'white', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M9 2L6 9h4l-1 5 5-7H10L9 2z" stroke={torchOn ? '#FFD94F' : 'white'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>
      </div>

      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '0 24px 28px', zIndex: 4, textAlign: 'center' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: statusLabel?.mainColor || 'white', marginBottom: 4 }}>
          {statusLabel?.main || (torchOn ? 'Torch on' : 'Scan a QR code')}
        </div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5, wordBreak: 'break-all' }}>
          {statusLabel?.sub || 'Point at any tool or inventory label'}
        </div>
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.08)', borderRadius: 20, padding: '5px 10px' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#378ADD' }} />
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>Tools</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.08)', borderRadius: 20, padding: '5px 10px' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#1D9E75' }} />
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>Inventory</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.08)', borderRadius: 20, padding: '5px 10px' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#EF9F27' }} />
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>Barcode</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function QRScanOverlay({ onClose, onScan }) {
  return ReactDOM.createPortal(
    <QRScanOverlayInner onClose={onClose} onScan={onScan} />,
    document.body
  );
}